import { useState } from 'react';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { entry } from '@/routes';
import { signUpWithEmail } from '@/lib/platform/auth';
import { enterAfterAuth } from '@/lib/platform/routing';

export function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignUp = async () => {
    setMessage(null);
    setIsSubmitting(true);
    const result = await signUpWithEmail(name, email, password);
    if (!result.ok) {
      setMessage(result.message);
      setIsSubmitting(false);
      return;
    }
    await enterAfterAuth(entry);
  };

  return (
    <main className="min-h-screen bg-background text-foreground selection:bg-primary/20">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl">
        <div className="hidden w-1/3 border-r border-border/70 bg-secondary/20 px-10 py-12 lg:flex lg:flex-col lg:justify-between xl:px-14">
          <div className="flex items-center gap-3 text-sm font-semibold tracking-tight">
            <span className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <BookOpen className="size-4" aria-hidden="true" />
            </span>
            Hive Register
          </div>
          <div>
            <p className="text-sm font-medium text-primary">Start with the facts</p>
            <p className="mt-4 max-w-xs text-2xl font-semibold tracking-[-0.025em]">
              A clear record makes good beekeeping easier.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">Your register, ready when you are.</p>
        </div>
        <section className="flex min-h-screen flex-1 items-center justify-center px-6 py-12 sm:px-10">
          <div className="w-full max-w-sm">
            <div className="mb-10 lg:hidden">
              <div className="flex items-center gap-3 text-sm font-semibold tracking-tight">
                <span className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                  <BookOpen className="size-4" aria-hidden="true" />
                </span>
                Hive Register
              </div>
            </div>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold tracking-[-0.03em]">Create your register</h1>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                Set up an account to keep your hive records together.
              </p>
            </div>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="sign-up-name">Your name</Label>
                <Input
                  id="sign-up-name"
                  data-jr-key="sign-up-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Alex Morgan"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sign-up-email">Email address</Label>
                <Input
                  id="sign-up-email"
                  data-jr-key="sign-up-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sign-up-password">Password</Label>
                <Input
                  id="sign-up-password"
                  data-jr-key="sign-up-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-describedby="password-hint"
                />
                <p id="password-hint" className="text-xs text-muted-foreground">
                  Use at least 8 characters.
                </p>
              </div>
              {message ? (
                <p
                  role="alert"
                  className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
                >
                  {message}
                </p>
              ) : null}
              <Button
                data-jr-key="sign-up-submit"
                className="h-10 w-full"
                disabled={isSubmitting}
                onClick={handleSignUp}
              >
                {isSubmitting ? 'Preparing register…' : 'Create account'}
                {!isSubmitting ? <ArrowRight className="ml-1 size-4" aria-hidden="true" /> : null}
              </Button>
            </div>
            <p className="mt-8 text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link
                data-jr-key="sign-in-link"
                to="/sign-in"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
